import React, { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, Minus, Plus, RotateCcw, X } from 'lucide-react';
import { useZoomPan } from './useZoomPan';
import { fotoUrl } from './checklists/checklistUtils';

// Telt open viewers, zodat StrictMode (effect twee keer) geen dubbele history-stap maakt.
let openViewers = 0;

// Terugknop van de telefoon sluit de viewer i.p.v. weg te navigeren.
const useTerugKnopSluit = (open, onClose) => {
  const sluit = useRef(onClose);
  sluit.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    openViewers += 1;
    if (!window.history.state?.fotoViewer) {
      window.history.pushState({ ...(window.history.state || {}), fotoViewer: true }, '');
    }
    const onPop = () => sluit.current();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      openViewers -= 1;
      setTimeout(() => {
        if (openViewers === 0 && window.history.state?.fotoViewer) window.history.back();
      }, 0);
    };
  }, [open]);
};

const KnopRond = ({ children, ...props }) => (
  <button type="button" {...props}
    className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors disabled:opacity-30">
    {children}
  </button>
);

const Bovenbalk = ({ idx, totaal, zp, onClose }) => (
  <div className="flex items-center justify-between gap-2 px-3 py-2 text-white" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
    <span className="text-sm font-medium tabular-nums min-w-[3.5rem]" data-testid="foto-viewer-teller">{idx + 1} / {totaal}</span>
    <div className="flex items-center gap-1.5">
      <KnopRond onClick={zp.uit} disabled={zp.t.s <= 1} aria-label="Uitzoomen" data-testid="foto-viewer-uitzoomen"><Minus className="w-5 h-5" /></KnopRond>
      <KnopRond onClick={zp.reset} disabled={zp.t.s === 1} aria-label="Zoom herstellen" data-testid="foto-viewer-reset"><RotateCcw className="w-4 h-4" /></KnopRond>
      <KnopRond onClick={zp.in} aria-label="Inzoomen" data-testid="foto-viewer-inzoomen"><Plus className="w-5 h-5" /></KnopRond>
      <KnopRond onClick={onClose} aria-label="Sluiten" data-testid="foto-viewer-sluiten"><X className="w-6 h-6" /></KnopRond>
    </div>
  </div>
);

const Pijl = ({ kant, onClick }) => (
  <button type="button" onClick={onClick} aria-label={kant === 'links' ? 'Vorige foto' : 'Volgende foto'}
    data-testid={kant === 'links' ? 'foto-viewer-vorige' : 'foto-viewer-volgende'}
    className={`absolute top-1/2 -translate-y-1/2 ${kant === 'links' ? 'left-2' : 'right-2'} z-10 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors`}>
    {kant === 'links' ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
  </button>
);

// Gedeelde fotoviewer (R7.1): zoom, pan, swipe, pijltjes en terugknop.
export const FotoViewer = ({ urls = [], index = null, onClose }) => {
  const open = index !== null && index !== undefined && urls.length > 0;
  const [idx, setIdx] = useState(index || 0);
  useEffect(() => { if (open) setIdx(Math.min(index, urls.length - 1)); }, [open, index, urls.length]);
  const totaal = urls.length;
  const ga = (stap) => setIdx(i => (i + stap + totaal) % totaal);
  const zp = useZoomPan(`${open}-${idx}`, (stap) => totaal > 1 && ga(stap));
  useTerugKnopSluit(open, onClose);
  // R9.2: eerst de w1600-variant; vanaf zoom 2× het origineel (blijft daarna staan).
  const [origineel, setOrigineel] = useState(false);
  useEffect(() => { setOrigineel(false); }, [open, idx]);
  useEffect(() => { if (zp.t.s >= 2) setOrigineel(true); }, [zp.t.s]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft' && totaal > 1) ga(-1);
    else if (e.key === 'ArrowRight' && totaal > 1) ga(1);
    else if (e.key === '+' || e.key === '=') zp.in();
    else if (e.key === '-') zp.uit();
    else if (e.key === '0') zp.reset();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-black/95" />
        <DialogPrimitive.Content aria-describedby={undefined} onKeyDown={onKeyDown}
          className="fixed inset-0 z-[201] flex flex-col outline-none" data-testid="foto-viewer">
          <DialogPrimitive.Title className="sr-only">Foto {idx + 1} van {totaal}</DialogPrimitive.Title>
          <Bovenbalk idx={idx} totaal={totaal} zp={zp} onClose={onClose} />
          <div ref={zp.ref} {...zp.handlers} data-testid="foto-viewer-podium"
            className="relative flex-1 overflow-hidden flex items-center justify-center select-none"
            style={{ touchAction: 'none', cursor: zp.t.s > 1 ? 'grab' : 'zoom-in' }}>
            {totaal > 1 && <Pijl kant="links" onClick={() => ga(-1)} />}
            {open && (
              <img src={origineel ? urls[idx] : fotoUrl(urls[idx], 1600)} alt={`Foto ${idx + 1}`} draggable={false}
                data-testid="foto-viewer-afbeelding" data-bron={origineel ? 'origineel' : 'w1600'}
                data-zoom={zp.t.s.toFixed(2)}
                className="max-w-full max-h-full object-contain pointer-events-none"
                style={{ transform: `translate(${zp.t.x}px, ${zp.t.y}px) scale(${zp.t.s})`, transition: zp.bezig ? 'none' : 'transform 150ms ease-out' }} />
            )}
            {totaal > 1 && <Pijl kant="rechts" onClick={() => ga(1)} />}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default FotoViewer;
